import { type CanvasElement, type CanvasElementId } from '@blaze-runtime/engine-domain';
import { type RenderElementContext } from '@blaze-shared/elements-domain';
import { type NamespaceId } from '@blaze-shared/workflow-domain';

import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import { DIALOG_SLOTS, type DialogElement } from '../../elements/dialog/dialog.element';
import { type DrawerElement } from '../../elements/drawer/drawer.element';
import { type RootElement } from '../../elements/root/root.element';

type RootLikeElement = RootElement | DialogElement | DrawerElement;
type NavSection = NonNullable<RenderElementContext['navSection']>;

const rootNavSlots = new Map<NavSection, 'navContainer' | 'navSidebar' | 'navBottombar'>([
  ['header', 'navContainer'],
  ['side', 'navSidebar'],
  ['bottom', 'navBottombar'],
]);

export class LayoutValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LayoutValidationError';
  }
}

function findElement(
  elementsMap: Map<CanvasElementId, CanvasElement>,
  elementId: CanvasElementId,
  contextId: CanvasElementId,
): CanvasElement {
  const element = elementsMap.get(elementId);
  if (!element) {
    throw new LayoutValidationError(
      `Invalid layout: Element "${elementId}" not found for child "${contextId}".`,
    );
  }
  return element;
}

function validateNavContainers(
  elementsMap: Map<CanvasElementId, CanvasElement>,
  rootElement: RootLikeElement,
): void {
  const getChildren = (root: RootLikeElement): CanvasElementId[] => {
    if (isElementType(root, 'root')) {
      const children: CanvasElementId[] = [];
      for (const slot of rootNavSlots.keys()) {
        children.push(...(root.slots[slot]?.children || []));
      }
      return children;
    }

    if (isElementType(root, 'dialog')) {
      const children: CanvasElementId[] = [];
      for (const slot of DIALOG_SLOTS) {
        children.push(...(root.slots[slot]?.children || []));
      }
      return children;
    }

    if (isElementType(root, 'drawer')) {
      return root.slots['content']?.children || [];
    }
    return [];
  };

  const children = getChildren(rootElement);

  for (const childElementId of children) {
    findElement(elementsMap, childElementId, rootElement.id);
  }
}

function validateRootLikeElement(
  elementsMap: Map<CanvasElementId, CanvasElement>,
  element: RootLikeElement,
): void {
  if (element.parentNode) {
    throw new LayoutValidationError(
      `Invalid layout: Element "${element.id}" should not have a parentNode.`,
    );
  }
  validateNavContainers(elementsMap, element);
}

function validateChildElement(
  elementsMap: Map<CanvasElementId, CanvasElement>,
  element: CanvasElement,
): void {
  if (!element.parentNode) {
    throw new LayoutValidationError(
      `Invalid layout: Element "${element.id}" is missing a parentNode.`,
    );
  }

  const parentElement = findElement(elementsMap, element.parentNode.id, element.id);
  const parentSlot = element.parentNode.slot
    ? parentElement.slots?.[element.parentNode.slot]
    : undefined;

  if (!parentSlot?.children.includes(element.id)) {
    throw new LayoutValidationError(
      `Invalid layout: Parent element "${parentElement.id}" in slot "${element.parentNode.slot}" does not contain child "${element.id}".`,
    );
  }
}

function validateParentChildLinks(elementsMap: Map<CanvasElementId, CanvasElement>): void {
  for (const element of elementsMap.values()) {
    if (isRootLikeElement(element)) {
      validateRootLikeElement(elementsMap, element);
    } else {
      validateChildElement(elementsMap, element);
    }
  }
}

function traverseForLoopsAndOrphans(
  element: CanvasElement,
  elementsMap: Map<CanvasElementId, CanvasElement>,
  processedElements: Set<CanvasElementId>,
): void {
  if (processedElements.has(element.id)) {
    throw new LayoutValidationError(
      `Invalid layout: Element with ID "${element.id}" is used in multiple places or creates an infinite loop.`,
    );
  }
  processedElements.add(element.id);

  if (!element.slots?.ids) {
    return;
  }

  for (const slotId of element.slots.ids) {
    const slot = element.slots[slotId];
    if (slot?.children) {
      for (const childId of slot.children) {
        const childElement = findElement(elementsMap, childId, element.id);
        traverseForLoopsAndOrphans(childElement, elementsMap, processedElements);
      }
    }
  }
}

function isRootLikeElement(element: CanvasElement): element is RootLikeElement {
  return (
    isElementType(element, 'root') ||
    isElementType(element, 'dialog') ||
    isElementType(element, 'drawer')
  );
}

export function validateLayout(elements: CanvasElement[]): void {
  if (elements.length === 0) {
    return;
  }

  const rootElements = new Map<NamespaceId, RootLikeElement>();
  for (const element of elements) {
    if (!isRootLikeElement(element)) {
      continue;
    }

    if (rootElements.has(element.ns)) {
      throw new LayoutValidationError(
        `Layout must have exactly one root element per namespace. Found: "${
          rootElements.get(element.ns)?.id
        }" and "${element.id}"`,
      );
    }

    rootElements.set(element.ns, element);
  }

  if (rootElements.size === 0) {
    throw new LayoutValidationError(
      'Layout must have exactly one root element per namespace. Found: 0',
    );
  }

  for (const rootElement of rootElements.values()) {
    const elementsMap = new Map<CanvasElementId, CanvasElement>(
      elements
        .filter((element) => element.ns === rootElement.ns)
        .map((element) => [element.id, element]),
    );

    validateParentChildLinks(elementsMap);

    const processedElements = new Set<CanvasElementId>();
    traverseForLoopsAndOrphans(rootElement, elementsMap, processedElements);

    if (processedElements.size !== elementsMap.size) {
      const unprocessedIds = elements
        .map((el) => el.id)
        .filter((id) => elementsMap.has(id) && !processedElements.has(id));

      throw new LayoutValidationError(
        `Orphaned elements detected. The following elements are not part of the layout tree: ${unprocessedIds.join(
          ', ',
        )}`,
      );
    }
  }
}
